/**
 * Start here (/start) — the welcome that follows onboarding.
 *
 * Was the third step of the old onboarding wizard. It is its own route now
 * because the two steps before it are a gate (you cannot use the app without
 * answering them) while this one is an invitation: a student who lands here is
 * fully set up, so /today is always a legitimate next click, and returning to
 * this page later still works.
 */
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { apiPost } from '@/lib/api';
import { Button } from '@/components/ds';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { useMyProfile } from '@/hooks/useSession';
import { fontPx } from '@/lib/fontSize';

const REFERENCE_PAPER_ID = 'paper_cbse10_mock1';

export function StartPage() {
  const navigate = useNavigate();
  const profile = useMyProfile(true);
  const firstName = profile.data?.firstName ?? '';

  const placement = useMutation({
    mutationFn: () => apiPost<{ id: string }>('/student-papers', { paperId: REFERENCE_PAPER_ID }),
    onSuccess: (sp) => navigate(`/exam/${sp.id}`),
  });

  // The placement paper only exists for CBSE Grade 10 today; everyone else is
  // welcomed straight to their home rather than offered a paper that 404s.
  const hasPlacement = profile.data?.board === 'CBSE' && profile.data?.grade === 'TEN';

  return (
    <AuthLayout width={640}>
      <div className="flex flex-col gap-5">
        <div>
          <span
            className="inline-block rounded-full px-3 py-1 text-xs font-semibold"
            style={{ background: 'var(--green-bg)', color: 'var(--green-text)' }}
          >
            You&rsquo;re all set{firstName ? `, ${firstName}` : ''} 🎉
          </span>
          <h1 className="mt-3 text-2xl font-bold text-forest">Let&rsquo;s find your starting point</h1>
          <p className="mt-1 text-ink-soft">
            {hasPlacement
              ? 'Take a short placement paper. We’ll grade it instantly and sort every concept into a colour — that’s where your journey begins. No marks are ever shared.'
              : 'Your home is ready. Take your first paper whenever you like — we’ll grade it instantly and sort every concept into a colour to set your starting point.'}
          </p>
        </div>

        {placement.isError && (
          <p className="text-sm text-danger">
            Couldn&rsquo;t start the paper. You can also begin from your home page.
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          {hasPlacement && (
            <Button
              style={{ padding: '12px 22px', fontSize: fontPx(15) }}
              disabled={placement.isPending}
              onClick={() => placement.mutate()}
            >
              {placement.isPending ? 'Starting…' : 'Start placement paper →'}
            </Button>
          )}
          <Button
            variant={hasPlacement ? 'secondary' : 'primary'}
            style={{ padding: '12px 22px', fontSize: fontPx(15) }}
            onClick={() => navigate('/today')}
          >
            {hasPlacement ? 'Skip to home' : 'Go to my home →'}
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}

export default StartPage;
